import { useRef } from 'react';
import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { Form, Input, PhoneNumber, Radio } from '@crisellevaldez/speculo-ui';
import { parsePhoneNumber } from 'react-phone-number-input';
import type { TwoFaContactType } from '../schemas/onboarding.schema';
import { FormSection } from './FormSection';
import type { User } from '../../../../hooks/useUser';

interface TwoFaContactFieldsProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  /**
   * Path prefix of the four `two_fa_contact_*` fields in `form`,
   * e.g. `crm_entries.0.` or `email_domain.`.
   */
  fieldPrefix: string;
  user: User | null;
  /** Completes the helper text: "…Two-Factor Authentication codes for {purpose}." */
  purpose: string;
}

// Stops typing once the number reaches its country's maximum length (10 digits
// after +1 for US/CA). PhoneNumber forwards this to react-phone-number-input but
// its prop types don't declare it.
const PHONE_INPUT_LIMIT = { limitMaxLength: true } as Record<string, unknown>;

/** The onboarding user's details, used when they pick "Me". Phone is E.164 for PhoneNumber. */
const getProfileContact = (user: User | null) => ({
  name: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim(),
  email: user?.email?.trim() ?? '',
  phone: user?.phoneNumber ? (parsePhoneNumber(user.phoneNumber, 'US')?.number ?? '') : '',
});

export const TwoFaContactFields = <T extends FieldValues>({
  form,
  fieldPrefix,
  user,
  purpose,
}: TwoFaContactFieldsProps<T>) => {
  const field = (key: 'type' | 'name' | 'email' | 'phone') =>
    `${fieldPrefix}two_fa_contact_${key}` as Path<T>;
  const errorFor = (key: 'type' | 'name' | 'email' | 'phone') =>
    form.getFieldState(field(key), form.formState).error?.message;
  const setField = (key: 'type' | 'name' | 'email' | 'phone', value: string) =>
    form.setValue(field(key), value as PathValue<T, Path<T>>, {
      shouldValidate: form.formState.isSubmitted,
    });
  const idPrefix = fieldPrefix.replace(/[^a-zA-Z0-9]+/g, '-');

  const contactType = form.watch(field('type')) as TwoFaContactType | undefined;
  const profile = getProfileContact(user);
  const values = {
    name: (form.watch(field('name')) as string | undefined) ?? '',
    email: (form.watch(field('email')) as string | undefined) ?? '',
    phone: (form.watch(field('phone')) as string | undefined) ?? '',
  };
  // "Me" fills the contact from the profile and hides those fields; a detail
  // the profile lacks, or one that fails validation, is still shown to fix.
  const isShown = (key: keyof typeof values) =>
    contactType === 'other' ||
    (contactType === 'me' &&
      (!(profile[key] && values[key] === profile[key]) || Boolean(errorFor(key))));
  const hasVisibleFields = isShown('name') || isShown('email') || isShown('phone');

  // "Someone else" details, kept while "Me" is selected so switching back
  // restores them (including details loaded from a previous save).
  const someoneElseRef = useRef(
    contactType === 'other' ? { ...values } : { name: '', email: '', phone: '' }
  );

  const selectType = (type: TwoFaContactType) => {
    if (type === contactType) return;
    if (contactType === 'other') someoneElseRef.current = { ...values };
    const source = type === 'me' ? profile : someoneElseRef.current;
    setField('type', type);
    setField('name', source.name);
    setField('email', source.email);
    setField('phone', source.phone);
  };

  return (
    <FormSection className="md:col-span-2">
      <Form.Control required>
        <Form.Label>2FA Contact</Form.Label>
        <Form.HelperText>
          The person who receives and manages Two-Factor Authentication codes for {purpose}.
        </Form.HelperText>
        <div className="flex flex-wrap gap-6 mt-3">
          <Radio
            id={`${idPrefix}two-fa-me`}
            name={`${idPrefix}two-fa-contact-type`}
            value="me"
            checked={contactType === 'me'}
            onChange={() => selectType('me')}
            label="Me"
          />
          <Radio
            id={`${idPrefix}two-fa-other`}
            name={`${idPrefix}two-fa-contact-type`}
            value="other"
            checked={contactType === 'other'}
            onChange={() => selectType('other')}
            label="Someone else"
          />
        </div>
        {errorFor('type') && <p className="mt-1 text-sm text-destructive">{errorFor('type')}</p>}
      </Form.Control>

      {hasVisibleFields && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {isShown('name') && (
            <Form.Control required>
              <Form.Label>Name</Form.Label>
              <Input
                {...form.register(field('name'))}
                placeholder="Full name"
                error={errorFor('name')}
              />
            </Form.Control>
          )}
          {isShown('email') && (
            <Form.Control required>
              <Form.Label>Email</Form.Label>
              <Input
                {...form.register(field('email'))}
                type="email"
                placeholder="name@company.com"
                error={errorFor('email')}
              />
            </Form.Control>
          )}
          {isShown('phone') && (
            <Form.Control required>
              <Form.Label>Phone</Form.Label>
              <PhoneNumber
                value={values.phone}
                onChange={(value) => setField('phone', value || '')}
                placeholder="Enter phone number"
                defaultCountry="US"
                {...PHONE_INPUT_LIMIT}
                error={errorFor('phone')}
              />
            </Form.Control>
          )}
        </div>
      )}
    </FormSection>
  );
};
